/**
 * src/components/onboarding/EmployeeLifecycleWizard.jsx
 * Guide Part 2 — walks users through the employee lifecycle (JML) module.
 * Populates sample employees, evaluation criteria, and assets so users can
 * see a fully-worked example they can explore and edit.
 */
import { useState } from 'react';
import WizardShell from '../shared/WizardShell.jsx';
import Button from '../shared/Button.jsx';
import { SAMPLE_EMPLOYEES, SAMPLE_EVALUATION_CRITERIA } from './employeeGuideData.js';
import { markSampleEmployee, markSampleCriterion } from '../../utils/sampleRecords.js';

/* ── reusable styles ──────────────────────────────────────────────────────── */
const heading = {
  fontSize: 22,
  fontWeight: 700,
  color: 'var(--fg)',
  marginBottom: 16,
  fontFamily: 'var(--font-display)',
};
const body = { fontSize: 14, color: 'var(--fg2)', lineHeight: 1.75, marginBottom: 24 };
const card = {
  padding: '12px 16px',
  marginBottom: 8,
  borderRadius: 8,
  background: 'var(--accent-subtle)',
  border: '1px solid var(--accent-border2)',
  fontSize: 13,
  color: 'var(--fg)',
  lineHeight: 1.6,
};

/* ── Steps ────────────────────────────────────────────────────────────────── */

function IntroStep() {
  return (
    <div>
      <h2 style={heading}>Part 2 — Employee Lifecycle</h2>
      <p style={body}>
        In Part 1 you created documents, risks, and tasks. Now we'll explore how Tryggr manages the{' '}
        <strong>people side</strong> of compliance — onboarding joiners, moving people between
        roles, and offboarding leavers. This is called the <strong>JML process</strong> (Joiner /
        Mover / Leaver), and it's one of the most audited areas in ISO 27001.
      </p>
      <p style={{ ...body, marginBottom: 32 }}>
        You can add three example employees at the end to see how each lifecycle stage works —
        complete with checklists, assets, evaluations, and sensitive-document handling.
      </p>
    </div>
  );
}

function JoinerStep() {
  return (
    <div>
      <h2 style={heading}>The Joiner Process</h2>
      <p style={body}>
        When someone new joins your organisation, there's a checklist of security tasks that must
        happen — contracts signed, background checks completed, systems provisioned, training
        assigned. Missing any of these is a compliance gap an auditor will flag.
      </p>
      <div style={card}>
        <strong>Maria Chen</strong> — Senior Developer who joined in June 2025. Her joiner checklist
        is fully completed: contract, NDA, screening, IT accounts, assets, security training, policy
        acknowledgement, and acceptable use agreement — all checked off with timestamps and notes.
      </div>
      <p style={{ fontSize: 13, color: 'var(--fg3)', lineHeight: 1.6, marginBottom: 24 }}>
        She also has 3 assets assigned (laptop, badge, YubiKey), has acknowledged all key policies,
        and went through the sensitive-document flow for her criminal record check.
      </p>
    </div>
  );
}

function MoverStep() {
  return (
    <div>
      <h2 style={heading}>The Mover Process</h2>
      <p style={body}>
        When someone changes role or department, their access rights need reviewing. Old permissions
        must be revoked, new ones granted, and knowledge transferred. Failure to do this is one of
        the most common access-control findings in audits.
      </p>
      <div style={card}>
        <strong>Thomas Andersen</strong> — Systems Administrator transferring from Operations to
        Security. His mover process is in progress: access reviewed, assets confirmed, new manager
        assigned — but role update, training review, and data handover are still pending.
      </div>
      <p style={{ fontSize: 13, color: 'var(--fg3)', lineHeight: 1.6, marginBottom: 24 }}>
        You'll see his status as "Moving" in the employee list, with a progress indicator showing 3
        of 6 checklist items completed.
      </p>
    </div>
  );
}

function LeaverStep() {
  return (
    <div>
      <h2 style={heading}>The Leaver Process</h2>
      <p style={body}>
        When someone leaves, it's critical that all access is revoked, assets returned, and
        knowledge transferred before their last day. An active account belonging to a departed
        employee is a serious security risk.
      </p>
      <div style={card}>
        <strong>Sarah Olsen</strong> — Financial Controller departing end of April 2026. Her exit
        interview is done, email forwarding set up, and legal obligations communicated — but asset
        return, access revocation, knowledge transfer, final payroll, and data cleanup are still
        outstanding.
      </div>
      <p style={{ fontSize: 13, color: 'var(--fg3)', lineHeight: 1.6, marginBottom: 24 }}>
        Her status shows as "Offboarding" with 3 of 8 items completed. You can open her record to
        check off remaining items as they're done.
      </p>
    </div>
  );
}

function ExtrasStep() {
  return (
    <div>
      <h2 style={heading}>Evaluations & Sensitive Documents</h2>
      <p style={body}>Beyond checklists, Tryggr tracks two more things per employee:</p>
      <div style={{ ...card, marginBottom: 12 }}>
        <strong>Evaluations</strong> — Rate employees against criteria you define (Security
        Awareness, Policy Compliance, Training Completion). Each criterion has its own scale and
        labels. Scores are tracked over time with history so you can show auditors that competency
        is being managed.
      </div>
      <div style={{ ...card, marginBottom: 12 }}>
        <strong>Sensitive documents</strong> — Things like criminal record checks and ID copies
        follow a strict <em>send → read → accept → delete</em> flow. The document itself is deleted
        after review, but the audit trail proves the check was performed. This satisfies GDPR data
        minimisation requirements.
      </div>
      <p style={{ fontSize: 13, color: 'var(--fg3)', lineHeight: 1.6, marginBottom: 24 }}>
        You can add three sample evaluation criteria at the end, and we've shown the full
        sensitive-document lifecycle on Maria's record.
      </p>
    </div>
  );
}

// Adding the sample people is an explicit opt-in on this step, never a side
// effect of Next or Finish: they are invented records written into a live
// tenant, so the user has to choose to add them. Finish without the opt-in
// leaves the workspace untouched. Every record added here carries the sample
// marker (GH #7870), so no evaluator counts it.
function SampleDataStep({ onApply, applied }) {
  return (
    <div>
      <h2 style={heading}>{applied ? 'Sample examples added' : 'Add sample examples?'}</h2>

      {!applied ? (
        <>
          <p style={body}>
            You can add 3 sample employees and 3 sample evaluation criteria to your workspace. They
            are marked <strong>Sample</strong> and never count as coverage or evidence. To record a
            real person, add a new employee — then delete the samples when you&apos;re done
            exploring. Or press <strong>Finish</strong> without adding them and explore on your own.
          </p>
          <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px 0' }}>
            {[
              'Maria Chen — active, completed joiner, assets, evaluations, sensitive docs',
              'Thomas Andersen — moving between departments, mover in progress',
              'Sarah Olsen — offboarding, leaver in progress',
              '3 evaluation criteria: Security Awareness, Policy Compliance, Training Completion',
            ].map((item, i) => (
              <li key={i} style={card}>
                <span style={{ color: 'var(--accent)', fontWeight: 700, marginRight: 8 }}>+</span>
                {item}
              </li>
            ))}
          </ul>
          <Button onClick={onApply}>Add sample examples to my workspace</Button>
        </>
      ) : (
        <>
          <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px 0' }}>
            {[
              '3 sample employees across joiner, mover, and leaver stages',
              '3 evaluation criteria with different scales',
              'Assets, document acknowledgements, and sensitive-document audit trails',
            ].map((item, i) => (
              <li key={i} style={card}>
                <span
                  style={{ color: 'var(--accent)', fontWeight: 700, marginRight: 8, fontSize: 16 }}
                >
                  ✓
                </span>
                {item}
              </li>
            ))}
          </ul>
          <p style={{ fontSize: 14, color: 'var(--fg2)', lineHeight: 1.75, margin: 0 }}>
            Head over to <strong>Employees</strong> in the sidebar to see them. Open any employee to
            explore their checklists, assets, evaluations, and document history.
          </p>
        </>
      )}
    </div>
  );
}

/* ── Main wizard ──────────────────────────────────────────────────────────── */
export default function EmployeeLifecycleWizard({
  state: _state,
  updateState,
  session: _session,
  onClose,
}) {
  const [applied, setApplied] = useState(false);

  const handleApply = () => {
    updateState((s) => {
      // Add employees (skip any whose ID already exists). Every seeded record
      // carries `sample: true` so no evaluator counts it (GH #7870).
      if (!s.employees) s.employees = [];
      const existingIds = new Set(s.employees.map((e) => e.id));
      for (const emp of SAMPLE_EMPLOYEES) {
        if (!existingIds.has(emp.id)) s.employees.push(markSampleEmployee(emp));
      }
      // Add evaluation criteria (skip any whose ID already exists)
      if (!s.employeeEvaluationCriteria) s.employeeEvaluationCriteria = [];
      const existingCritIds = new Set(s.employeeEvaluationCriteria.map((c) => c.id));
      for (const crit of SAMPLE_EVALUATION_CRITERIA) {
        if (!existingCritIds.has(crit.id))
          s.employeeEvaluationCriteria.push(markSampleCriterion(crit));
      }
    });
    setApplied(true);
  };

  const steps = [
    { id: 'intro', label: 'Introduction', body: <IntroStep /> },
    { id: 'joiner', label: 'Joiner', body: <JoinerStep /> },
    { id: 'mover', label: 'Mover', body: <MoverStep /> },
    { id: 'leaver', label: 'Leaver', body: <LeaverStep /> },
    { id: 'extras', label: 'Evaluations & documents', body: <ExtrasStep /> },
    {
      id: 'samples',
      label: 'Sample examples',
      body: <SampleDataStep onApply={handleApply} applied={applied} />,
    },
  ];

  return (
    <WizardShell
      title="Guide — Employee lifecycle"
      steps={steps}
      onClose={onClose}
      onFinish={onClose}
      dirty={false}
    />
  );
}
